import React, { memo, useState } from "react";
import { Edit2, ArrowRightLeft, Siren, Layers, Ruler, Gem, ShieldCheck, PenTool, MessageSquare, Send, ExternalLink, Disc } from "lucide-react";
import DaysBadge from "./DaysBadge";
import TransitToggle from "./TransitToggle";
import EngravingDisplay from "./EngravingDisplay";
import { PRODUCTION_STATUS_CONFIG, KANBAN_ORDER, isStatusAguardandoInjecao, isStatusFaseCeraMolde } from "../../config/productionStatuses";
import { productionService } from "../../services/productionService";
import { useCommunication } from "../../contexts/CommunicationContext";
import { useCatalogContext } from "../../contexts/CatalogContext";
import { cleanSku, getBaseSku } from "../../utils/skuUtils";

// Helper Anti-ND
const hasValue = (val) => {
  if (!val) return false;
  const s = String(val).trim().toUpperCase();
  return s !== "" && s !== "ND" && s !== "-" && s !== "N/A";
};

const OrderItemRow = memo(({
  user,
  subItem,
  onEditItem,
  onMoveItem,
  onToggleTransit,
  onUpdateStatus
}) => {
  const { openQuickProduct } = useCommunication();
  const { isMoldeSku, findItem } = useCatalogContext();
  const [isMuralOpen, setIsMuralOpen] = useState(false);
  const [newMessageText, setNewMessageText] = useState("");
  const [isSending, setIsSending] = useState(false);

  const catalog = findItem ? findItem(subItem.sku) : null;
  const cleanPai = getBaseSku(cleanSku(subItem.sku));
  const isMolde = Boolean(
    subItem.tem_molde ||
    subItem.full_data?.tem_molde ||
    subItem.specs?.tem_molde ||
    catalog?.tem_molde ||
    catalog?.full_data?.tem_molde ||
    catalog?.full_data?.processo_fabricacao === "borracha" ||
    (isMoldeSku && (isMoldeSku(subItem.sku) || isMoldeSku(cleanPai)))
  );
  // A tag BORRACHA só é exibida durante as etapas de cera/molde (pré-injeção e injetado aguardando árvore)
  const hasBorracha = isMolde && isStatusFaseCeraMolde(subItem.status);

  const messages = subItem.full_data?.messages || subItem.messages || [];
  const totalMessages = messages.length;
  const hasUserComments = messages.some(m => m.type === "user");

  const handleSendMessage = async () => {
    const text = newMessageText.trim();
    if (!text || isSending) return;

    setIsSending(true);
    try {
      const authorName = user?.name || "Usuário";
      await productionService.addMuralMessage(subItem.id, text, "user", authorName);
      setNewMessageText("");
    } catch (e) {
      console.error("Erro ao enviar mensagem para o mural", e);
      alert("Erro ao enviar mensagem: " + e.message);
    } finally {
      setIsSending(false);
    }
  };

  const statusConf = PRODUCTION_STATUS_CONFIG[subItem.status] || { color: "bg-gray-200 text-gray-700" };
  const isNatural = String(subItem.specs?.stoneType || "").toLowerCase().includes("natural");
  const isIntercepted = subItem.isInterceptedPE;
  const isPE = subItem.isPE;

  return (
    <div className="flex flex-col rounded bg-white border border-slate-200 transition-all overflow-hidden">
      {/* CORPO DO ITEM */}
      <div className="flex flex-col xl:flex-row xl:flex-wrap xl:items-center justify-between p-2 gap-2">
        <div className="flex items-center gap-3">
          {/* --- NOVO: BADGE DE DIAS DO ITEM --- */}
          <div className="scale-100 origin-left -mr-1">
            <DaysBadge date={subItem.customCreatedAt || subItem.createdAt} order={subItem} />
          </div>
          {/* ----------------------------------- */}
          <div className="flex gap-1 items-center">
            <button
              onClick={() => onEditItem(subItem)}
              className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
              title="Editar Item"
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => onMoveItem(subItem)}
              className="p-1 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded"
              title="Mover Item"
            >
              <ArrowRightLeft size={14} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setIsMuralOpen(!isMuralOpen); }}
              className={`p-1 rounded transition-all flex items-center gap-0.5 ${
                isMuralOpen 
                  ? "text-purple-600 bg-purple-50 hover:bg-purple-100" 
                  : hasUserComments 
                    ? "text-purple-600 bg-purple-50/50 hover:bg-purple-50"
                    : "text-slate-400 hover:text-purple-600 hover:bg-purple-50"
              }`}
              title="Abrir Mural de Mensagens"
            >
              <MessageSquare size={14} />
              {totalMessages > 0 && (
                <span className="text-[9px] font-bold px-1 rounded-full bg-purple-100 text-purple-700 min-w-[14px] text-center">
                  {totalMessages}
                </span>
              )}
            </button>
          </div>

          <div className="flex gap-1 flex-wrap">
            {/* --- 3. BADGE DE EXCEÇÃO (ITEM) --- */}
            {hasValue(subItem.exception) && (
              <div
                className="bg-red-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded shadow-sm flex items-center gap-1 animate-pulse"
                title={subItem.exception}
              >
                <Siren size={8} className="text-white" />
                <span className="uppercase max-w-[100px] truncate">
                  {subItem.exception}
                </span>
              </div>
            )}
            {/* ---------------------------------- */}

            {/* Badges de Status */}
            {hasBorracha && (
              <span
                className="bg-amber-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded shadow-sm flex items-center gap-0.5 uppercase tracking-wider"
                title="Item de Molde/Borracha (Injeção Direta — sem impressão 3D)"
              >
                <Disc size={8} className="text-amber-100 animate-spin-slow" /> BORRACHA
              </span>
            )}
            {subItem.fromStock && !isPE && !isIntercepted && (
              <span className="bg-emerald-100 text-emerald-700 text-[9px] font-bold px-1 rounded cursor-help">
                E
              </span>
            )}
            {isIntercepted && (
              <span
                className="bg-orange-100 text-orange-700 text-[9px] font-bold px-1 rounded border border-orange-200 flex items-center gap-0.5"
                title="Interceptado"
              >
                <Layers size={8} /> INT
              </span>
            )}
            {isPE && !isIntercepted && (
              <span
                className="bg-purple-100 text-purple-700 text-[9px] font-bold px-1 rounded flex items-center gap-0.5"
                title="Produção Estoque"
              >
                <Layers size={8} /> PE
              </span>
            )}
            {subItem.printed && (
              <span className="bg-amber-100 text-amber-700 text-[9px] font-bold px-1 rounded cursor-help">
                I
              </span>
            )}
            {subItem.certificatePrinted && (
              <span className="bg-blue-100 text-blue-700 text-[9px] font-bold px-1 rounded cursor-help">
                C
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openQuickProduct(subItem.sku);
            }}
            className="font-mono text-xs font-bold text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-1.5 py-0.5 rounded border border-blue-200/60 transition cursor-pointer active:scale-95 flex items-center gap-1 group/sku"
            title="Abrir Ficha Rápida do Produto (Variações & Estoque)"
          >
            <span>{subItem.sku}</span>
            <ExternalLink size={10} className="opacity-0 group-hover/sku:opacity-100 transition text-blue-500 shrink-0" />
          </button>

          {/* SPECS REFINADAS */}
          <div className="flex items-center gap-1.5 text-xs text-slate-600 flex-wrap">
            {hasValue(subItem.specs?.size) && (
              <div
                className="flex items-center gap-1 bg-slate-50 px-1.5 rounded border border-slate-100"
                title="Aro"
              >
                <Ruler size={10} className="text-slate-400" />{" "}
                <b>{subItem.specs.size}</b>
              </div>
            )}

            {hasValue(subItem.specs?.stoneColor) && (
              <div
                className="flex items-center gap-1 bg-slate-50 px-1.5 rounded border border-slate-100"
                title="Cor"
              >
                <Gem size={10} className="text-purple-400" />{" "}
                <span className="font-bold">{subItem.specs.stoneColor}</span>
              </div>
            )}

            {hasValue(subItem.specs?.stoneBatch) && (
              <span className="bg-blue-50 text-blue-700 px-1.5 rounded text-[10px] border border-blue-100 font-mono">
                #{subItem.specs.stoneBatch}
              </span>
            )}

            {isNatural && (
              <span className="bg-indigo-50 text-indigo-700 px-1.5 rounded text-[10px] font-bold border border-indigo-100 flex items-center gap-1">
                <ShieldCheck size={8} />
                Natural
              </span>
            )}

            {hasValue(subItem.specs?.engraving) && (
              <EngravingDisplay rawText={subItem.specs.engraving} mode="compact" />
            )}
            {/* --- AQUI ENTRA O TOGGLE --- */}
            <div className="mr-2">
              <TransitToggle
                order={subItem}
                onToggle={onToggleTransit}
              />
            </div>
            {/* --------------------------- */}
          </div>
        </div>

        <select
          className={`text-[10px] font-bold px-2 py-1 rounded uppercase cursor-pointer outline-none text-center w-full xl:w-auto ${statusConf.color}`}
          value={subItem.status}
          onChange={(e) => onUpdateStatus(subItem.id, e.target.value)}
        >
          {KANBAN_ORDER.map((key) => (
            <option
              key={key}
              value={key}
              className="bg-white text-slate-800 font-normal"
            >
              {PRODUCTION_STATUS_CONFIG[key]?.label || key}
            </option>
          ))}
        </select>
      </div>

      {/* SEÇÃO DO MURAL */}
      {isMuralOpen && (
        <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-3 flex flex-col gap-3 animate-in slide-in-from-top duration-200">
          <div className="flex justify-between items-center">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <MessageSquare size={14} className="text-purple-500" /> Mural de Mensagens &amp; Histórico
            </h4>
            <span className="text-[10px] text-slate-400 font-medium">
              {totalMessages} registro{totalMessages !== 1 ? "s" : ""}
            </span>
          </div>

          {/* LISTA DE MENSAGENS */}
          <div 
            className="flex flex-col gap-2 max-h-60 overflow-y-auto pr-1 custom-scrollbar scroll-smooth"
            ref={(el) => {
              if (el) {
                el.scrollTop = el.scrollHeight;
              }
            }}
          >
            {messages.length === 0 ? (
              <div className="text-center py-6 text-slate-400 text-xs italic">
                Nenhuma mensagem ou log registrado para este pedido.
              </div>
            ) : (
              messages.map((msg) => {
                const isSystem = msg.type === "system";
                const dateStr = msg.timestamp ? new Date(msg.timestamp).toLocaleString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit"
                }) : "";

                if (isSystem) {
                  return (
                    <div key={msg.id} className="flex justify-center my-0.5">
                      <div className="bg-slate-100 border border-slate-200 text-slate-600 rounded px-2.5 py-1 text-[11px] max-w-full text-center flex items-center gap-1.5 leading-relaxed font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                        <span>{msg.text}</span>
                        <span className="text-[9px] text-slate-400 font-mono ml-1">{dateStr}</span>
                      </div>
                    </div>
                  );
                }

                // User comment bubble
                const isCurrentUser = user && msg.author && (msg.author.toLowerCase() === user.name?.toLowerCase());
                return (
                  <div key={msg.id} className={`flex flex-col gap-0.5 max-w-[85%] ${isCurrentUser ? "self-end items-end" : "self-start items-start"}`}>
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 px-1">
                      <span>{msg.author || "Usuário"}</span>
                      <span className="text-[8px] font-normal text-slate-400 font-mono">{dateStr}</span>
                    </div>
                    <div className={`rounded-xl px-3 py-2 text-xs shadow-sm leading-relaxed ${
                      isCurrentUser 
                        ? "bg-purple-600 text-white rounded-tr-none" 
                        : "bg-white border border-slate-200 text-slate-700 rounded-tl-none"
                    }`}>
                      {msg.text}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* INPUT PARA ENVIAR MENSAGEM */}
          <div className="flex gap-2 items-center mt-1 border-t border-slate-200 pt-2.5">
            <input
              type="text"
              placeholder="Escreva uma observação..."
              className="flex-1 bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-purple-500 shadow-inner text-slate-800"
              value={newMessageText}
              onChange={(e) => setNewMessageText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={isSending}
            />
            <button
              onClick={handleSendMessage}
              disabled={isSending || !newMessageText.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg p-1.5 transition-all shadow-sm hover:shadow disabled:bg-purple-400 disabled:cursor-not-allowed cursor-pointer shrink-0 flex items-center justify-center"
              title="Enviar observação"
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default OrderItemRow;
